import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, Power } from 'lucide-react';
import { clsx } from 'clsx';
import api from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';
import { formatApiErrorMessage } from '@/utils/apiErrorMessage';

type AiFeatureKey =
  | 'search'
  | 'assistant'
  | 'suggest'
  | 'summarize'
  | 'youtube'
  | 'bookmarkContext'
  | 'resurface'
  | 'aiManager';

type AiManagerIntensity = 'off' | 'gentle' | 'active';

interface AiPreferencesPayload {
  master: boolean;
  features: Record<AiFeatureKey, boolean>;
  telemetryOptIn: boolean;
  excludeWorkspaces?: string[];
  aiManagerIntensity?: AiManagerIntensity;
}

const FEATURE_KEYS: AiFeatureKey[] = [
  'search',
  'assistant',
  'suggest',
  'summarize',
  'youtube',
  'bookmarkContext',
  'resurface',
  'aiManager',
];

const DEFAULTS: AiPreferencesPayload = {
  master: true,
  features: {
    search: true,
    assistant: true,
    suggest: true,
    summarize: true,
    youtube: true,
    bookmarkContext: true,
    resurface: true,
    aiManager: true,
  },
  telemetryOptIn: false,
  aiManagerIntensity: 'gentle',
};

interface AiPreferencesPanelProps {
  /** When true, omit page-level padding and use profile-section styling. */
  embedded?: boolean;
}

/**
 * Master switch, per-feature AI toggles, and telemetry opt-in.
 * Embedded on Profile; legacy `/settings/ai-preferences` redirects here.
 */
export const AiPreferencesPanel = ({ embedded = false }: AiPreferencesPanelProps) => {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { patchLocalPreference } = useAuth();
  const { canManageAccount } = useAccountRole();
  const readOnlyOrg = !canManageAccount;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<AiPreferencesPayload>(DEFAULTS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ preferences: AiPreferencesPayload }>('/ai/preferences');
      const p = res.data?.preferences;
      if (p) {
        setPrefs({
          master: p.master ?? true,
          features: { ...DEFAULTS.features, ...(p.features ?? {}) },
          telemetryOptIn: p.telemetryOptIn ?? false,
          excludeWorkspaces: Array.isArray(p.excludeWorkspaces) ? p.excludeWorkspaces : [],
          aiManagerIntensity:
            p.aiManagerIntensity === 'off' ||
            p.aiManagerIntensity === 'gentle' ||
            p.aiManagerIntensity === 'active'
              ? p.aiManagerIntensity
              : DEFAULTS.aiManagerIntensity,
        });
      }
    } catch (e) {
      setError(formatApiErrorMessage(e, tCommon, t('aiPrefs.loadError', 'Could not load AI preferences.')));
    } finally {
      setLoading(false);
    }
  }, [t, tCommon]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyLocalLegacySync = useCallback(
    (featurePatch: Partial<Record<AiFeatureKey, boolean>>) => {
      const local: Partial<{ autoBookmarkContext: boolean; aiAutoSuggestGroupTags: boolean }> = {};
      if (typeof featurePatch.suggest === 'boolean') {
        local.aiAutoSuggestGroupTags = featurePatch.suggest;
      }
      if (typeof featurePatch.bookmarkContext === 'boolean') {
        local.autoBookmarkContext = featurePatch.bookmarkContext;
      }
      if (Object.keys(local).length > 0) {
        patchLocalPreference(local);
      }
    },
    [patchLocalPreference]
  );

  const persist = useCallback(
    async (next: {
      master?: boolean;
      features?: Partial<Record<AiFeatureKey, boolean>>;
      telemetryOptIn?: boolean;
      excludeWorkspaces?: string[];
      aiManagerIntensity?: AiManagerIntensity;
    }): Promise<boolean> => {
      if (readOnlyOrg) return false;
      setSaving(true);
      setError(null);
      try {
        const res = await api.put<{ preferences: AiPreferencesPayload }>('/ai/preferences', next);
        const p = res.data?.preferences;
        if (p) {
          setPrefs({
            master: p.master ?? true,
            features: { ...DEFAULTS.features, ...(p.features ?? {}) },
            telemetryOptIn: p.telemetryOptIn ?? false,
            excludeWorkspaces: Array.isArray(p.excludeWorkspaces) ? p.excludeWorkspaces : [],
            aiManagerIntensity:
              p.aiManagerIntensity === 'off' ||
              p.aiManagerIntensity === 'gentle' ||
              p.aiManagerIntensity === 'active'
                ? p.aiManagerIntensity
                : DEFAULTS.aiManagerIntensity,
          });
        }
        if (next.features) {
          applyLocalLegacySync(next.features);
        }
        return true;
      } catch (e) {
        setError(formatApiErrorMessage(e, tCommon, t('aiPrefs.saveError', 'Could not save AI preferences.')));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [applyLocalLegacySync, readOnlyOrg, t, tCommon]
  );

  const toggleMaster = (next: boolean) => {
    if (readOnlyOrg || saving) return;
    const snapshot = prefs;
    setPrefs((p) => ({ ...p, master: next }));
    void persist({ master: next }).then((ok) => {
      if (!ok) setPrefs(snapshot);
    });
  };

  const toggleFeature = (k: AiFeatureKey, next: boolean) => {
    if (readOnlyOrg || saving) return;
    const snapshot = prefs;
    setPrefs((p) => ({ ...p, features: { ...p.features, [k]: next } }));
    void persist({ features: { [k]: next } }).then((ok) => {
      if (!ok) setPrefs(snapshot);
    });
  };

  const toggleTelemetry = (next: boolean) => {
    if (readOnlyOrg || saving) return;
    const snapshot = prefs;
    setPrefs((p) => ({ ...p, telemetryOptIn: next }));
    void persist({ telemetryOptIn: next }).then((ok) => {
      if (!ok) setPrefs(snapshot);
    });
  };

  const setIntensity = (intensity: AiManagerIntensity) => {
    if (readOnlyOrg || saving) return;
    const snapshot = prefs;
    setPrefs((p) => ({ ...p, aiManagerIntensity: intensity }));
    void persist({
      aiManagerIntensity: intensity,
      features: { aiManager: intensity !== 'off' },
    }).then((ok) => {
      if (!ok) setPrefs(snapshot);
    });
  };

  const wrapperClass = embedded ? 'space-y-4' : 'mx-auto max-w-3xl space-y-6 px-4 py-8 md:px-8';

  return (
    <section id="ai-privacy" className={wrapperClass}>
      <header className="flex items-baseline gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <ShieldCheck size={16} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            className={
              embedded
                ? 'text-base font-bold text-foreground'
                : 'text-2xl font-bold tracking-tight text-foreground'
            }
          >
            {t('aiPrefs.title', 'AI privacy & controls')}
          </h3>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {t(
              'aiPrefs.subtitle',
              'Choose which AI features run on your data. Changes apply immediately on every device.'
            )}
          </p>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-5 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('aiPrefs.loading', 'Loading…')}
        </div>
      ) : (
        <div className="space-y-4">
          {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {error ? (
            <div className="border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}

          <div className="flex items-start justify-between gap-4 p-5">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Power className="h-4 w-4 text-primary" aria-hidden />
                {t('aiPrefs.master.title', 'Master AI switch')}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                {t(
                  'aiPrefs.master.description',
                  'When off, no AI request is sent for your account. The UI stays visible but every AI button shows "AI is off".'
                )}
              </p>
            </div>
            <Toggle
              checked={prefs.master}
              onChange={toggleMaster}
              disabled={readOnlyOrg || saving}
              ariaLabel={t('aiPrefs.master.label', 'Enable AI features')}
            />
          </div>

          <div className={clsx('border-t border-border-muted', !prefs.master && 'opacity-60')}>
            <p className="px-5 pb-2 pt-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
              {t('aiPrefs.features.title', 'Per-feature toggles')}
            </p>
            <div className="divide-y divide-border/40 px-2 pb-2">
              {FEATURE_KEYS.map((k) => (
                <FeatureRow
                  key={k}
                  featureKey={k}
                  enabled={prefs.features[k]}
                  disabled={readOnlyOrg || !prefs.master || saving}
                  onChange={(next) => toggleFeature(k, next)}
                />
              ))}
            </div>
          </div>

          <div className={clsx('border-t border-border-muted px-5 py-4', !prefs.master && 'opacity-60')}>
            <p className="text-sm font-semibold text-foreground">
              {t('aiPrefs.aiManager.title', 'AI Task Manager')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t(
                'aiPrefs.aiManager.description',
                'Automated check-ins on overdue tasks. Manual check-ins always work.'
              )}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(['off', 'gentle', 'active'] as const).map((level) => (
                <button
                  key={level}
                  type="button"
                  disabled={readOnlyOrg || !prefs.master || saving}
                  onClick={() => setIntensity(level)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors',
                    prefs.aiManagerIntensity === level
                      ? 'bg-primary/10 border-primary/40 text-primary'
                      : 'border-border text-muted-foreground hover:border-border/80'
                  )}
                >
                  {t(`aiPrefs.aiManager.intensity.${level}`, level)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-start justify-between gap-4 border-t border-border-muted p-5">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                {t('aiPrefs.telemetry.title', 'Help improve AI quality')}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                {t(
                  'aiPrefs.telemetry.description',
                  'Send anonymous metrics about model latency and answer length. We never share your content.'
                )}
              </p>
            </div>
            <Toggle
              checked={prefs.telemetryOptIn}
              onChange={toggleTelemetry}
              disabled={readOnlyOrg || saving}
              ariaLabel={t('aiPrefs.telemetry.label', 'Share anonymous AI quality metrics')}
            />
          </div>
        </div>
        </div>
      )}
    </section>
  );
};

interface FeatureRowProps {
  featureKey: AiFeatureKey;
  enabled: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}

const FEATURE_LABELS: Record<
  AiFeatureKey,
  { titleKey: string; titleFallback: string; descKey: string; descFallback: string }
> = {
  search: {
    titleKey: 'aiPrefs.feature.search',
    titleFallback: 'AI Search',
    descKey: 'aiPrefs.feature.searchDesc',
    descFallback: 'Semantic search across your library.',
  },
  assistant: {
    titleKey: 'aiPrefs.feature.assistant',
    titleFallback: 'AI Assistant',
    descKey: 'aiPrefs.feature.assistantDesc',
    descFallback: 'Chat with your library content.',
  },
  suggest: {
    titleKey: 'aiPrefs.feature.suggest',
    titleFallback: 'Smart suggestions',
    descKey: 'aiPrefs.feature.suggestDesc',
    descFallback: 'Title, tags, and group suggestions while saving bookmarks.',
  },
  summarize: {
    titleKey: 'aiPrefs.feature.summarize',
    titleFallback: 'One-click summaries',
    descKey: 'aiPrefs.feature.summarizeDesc',
    descFallback: 'Summarize bookmarks, notes, and excerpts.',
  },
  youtube: {
    titleKey: 'aiPrefs.feature.youtube',
    titleFallback: 'YouTube AI (Pro+)',
    descKey: 'aiPrefs.feature.youtubeDesc',
    descFallback: 'Transcript-based AI on YouTube videos.',
  },
  bookmarkContext: {
    titleKey: 'aiPrefs.feature.bookmarkContext',
    titleFallback: 'Background bookmark context (Pro+)',
    descKey: 'aiPrefs.feature.bookmarkContextDesc',
    descFallback: 'Auto-enrich new bookmarks with summaries and intent.',
  },
  resurface: {
    titleKey: 'aiPrefs.feature.resurface',
    titleFallback: 'Library resurfacing (Pro+)',
    descKey: 'aiPrefs.feature.resurfaceDesc',
    descFallback: 'Remind you when a page relates to something you saved before.',
  },
  aiManager: {
    titleKey: 'aiPrefs.feature.aiManager',
    titleFallback: 'AI Task Manager',
    descKey: 'aiPrefs.feature.aiManagerDesc',
    descFallback: 'Proactive task check-ins and progress nudges.',
  },
};

const FeatureRow = ({ featureKey, enabled, disabled, onChange }: FeatureRowProps) => {
  const { t } = useTranslation('settings');
  const labels = FEATURE_LABELS[featureKey];
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{t(labels.titleKey, labels.titleFallback)}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t(labels.descKey, labels.descFallback)}</p>
      </div>
      <Toggle
        checked={enabled}
        onChange={onChange}
        disabled={disabled}
        ariaLabel={t(labels.titleKey, labels.titleFallback)}
      />
    </div>
  );
};

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  ariaLabel: string;
}

const Toggle = ({ checked, onChange, disabled, ariaLabel }: ToggleProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={
      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 ' +
      (checked ? 'bg-primary' : 'bg-muted')
    }
  >
    <span
      aria-hidden
      className={
        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ' +
        (checked ? 'translate-x-5' : 'translate-x-0')
      }
    />
  </button>
);

export default AiPreferencesPanel;
